<template lang="pug">
div
  //- Fetching
  template(v-if="isFetching")
    v-skeleton-loader.px-4(type="list-item-three-line@5")
  template(v-else)
    //- Navigation tabs
    div.d-flex.align-center.text-sm
      v-btn.ml-n3.mr-2(text x-small icon @click="toPatientDatabase")
        v-icon(color="neutral700" :size="20") mdi-arrow-left
      v-hover(v-slot="{ hover }")
        span.font-medium.clickable.px-2.round-lg(:class="[{ 'neutral300':hover }]" @click="toPatientDatabase") {{ $t('patient_database') }}
      v-icon.mx-2(small) mdi-chevron-right
      span.font-regular {{ $t('patient_profile') }}
    //- Profile items
    .d-flex.mt-4
      .mr-4
        //- Profile image
        v-img.round-xl(v-if="patient.profile_image" width="60" height="80" :src="patient.profile_image.file" cover)
          template(v-slot:placeholder)
            v-row(class="fill-height ma-0" align="center" justify="center")
              v-progress-circular(indeterminate color="grey")
        v-card.neutral300.fill-height.round-xl(v-else flat width="60" height="80")
          div.d-flex.align-center.justify-center.fill-height
            v-icon(size="30") $yzi-user-03
      //- Profile info
      div
        .text-lg.font-semibold.mb-1 {{ patient.name }}
        .text-sm {{ patient.ic }}
        .text-sm(v-if="patient.dob") {{ $options.filters.getAgeByYearMonthDay(patient.dob) | displayAgeByYearMonthDay }} old
    //- Analytics
    v-card.rounded-lg.mt-6.neutral200.analytic-border(outlined)
      v-row(no-gutters)
        v-col.text-center.pa-2
          .text-xs.neutral500--text {{ $t('total_spend') }}
          .text-sm.no-wrap {{ patient.lifetime_value | displayPrice }}
        v-divider(vertical)
        v-col.text-center.pa-2
          .text-xs.neutral500--text {{ $t('total_visit') }}
          .text-sm.no-wrap {{ patient.total_visit }}
        v-divider(vertical)
        v-col.text-center.pa-2
          .text-xs.neutral500--text {{ $t('since') }}
          .text-sm.no-wrap {{ patient.since }}
    //- Patient details
    div.mt-8
      .d-flex.justify-space-between.align-center.mb-4
        .text-md.font-semibold {{ $t('patient_details') }}
        //- Company dropdown
        company-dropdown(:companies="availableCompanies" :selected-company="selectedCompany" @company-selected="onCompanySelected")
      template(v-for="detail in detailItems")
        .mb-3
          .text-sm.neutral500--text.font-semibold {{ detail.text }}
          .text-sm {{ patient[detail.value] }}
</template>

<script>
import { DateTime } from 'luxon'
import { mapGetters, mapActions } from 'vuex'
import CompanyDropdown from '@/components/hq/patient/CompanyDropdown.vue'

export default {
  components: {
    CompanyDropdown
  },
  data() {
    return {
      isFetching: true,
      patient: null,
      allDetails: [],
      selectedCompany: null,
      route: { name: 'HQPatientList' },
      detailItems: [
        {
          text:'Email',
          value:'email',
        },
        {
          text:'Phone',
          value:'phone',
        },
        {
          text:'Birthday',
          value:'formatted_birthday',
        },
        {
          text:'Gender',
          value:'capitalize_gender',
        },
        {
          text:'Nationality',
          value:'capitalize_nationality',
        },
        {
          text:'Race',
          value:'capitalize_race',
        },
        {
          text:'Religion',
          value:'capitalize_religion',
        },
        {
          text:'Address',
          value:'full_address',
        },
      ],
      openAddPatientDialog: false
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
    }),
    patientId () {
      return this.$route.params.id
    },
    availableCompanies () {
      if (!this.allDetails.length) return []

      // Extract unique companies from allDetails
      const companiesMap = new Map()
      this.allDetails.forEach(patient => {
        if (patient.company && !companiesMap.has(patient.company.id)) {
          companiesMap.set(patient.company.id, patient.company)
        }
      })

      return Array.from(companiesMap.values())
    }
  },
  mounted () {
    this.fetchPatient()
    this.saveRoute()
    this.setPopState()
  },
  beforeDestroy () {
    window.removeEventListener('popstate', this.handlePopState)
  },
  methods: {
    ...mapActions(['showMessage']),
    saveRoute () {
      if (this.$route.params && this.$route.params.data && Object.keys(this.$route.params.data).length) {
        this.route.params = { ...this.$route.params.data }
      }
    },
    setPopState () {
      history.pushState(null, document.title, window.location.href)
      window.addEventListener('popstate', this.handlePopState)
    },
    displayDate (value) {
      if (!value) {
        return null
      }
      const date = DateTime.fromISO(value)
      return `${date.toFormat('dd MMM yyyy')}`
    },
    getAddressInString (addr) {
      if (!addr) {
        return null
      }
      let address = ''
      if (addr.address_1) {
        address += addr.address_1
      } if (addr.address_2) {
        address += `, ${addr.address_2}`
      } if (addr.postcode) {
        address += `, ${addr.postcode}`
      } if (addr.city) {
        address += `, ${addr.city}`
      } if (addr.state) {
        address += `, ${addr.state}`
      } if (addr.country) {
        address += `, ${addr.country}`
      }
      return address
    },
    toPatientDatabase () {
      return this.$router.push(this.route)
    },
    async fetchPatient () {
      try {
        this.isFetching = true
        const params = {
          uid: this.patientId,
          expand: 'company'
        }
        const { data } = await this.$axios.get(`/clinics/${this.company.slug}/hq/patients`, { params })
        this.allDetails = data.results.length ? data.results[0].patients : []

        // Initialize with first company if multiple companies exist
        if (this.availableCompanies.length > 0) {
          this.selectedCompany = this.availableCompanies[0]
          this.updatePatientData()
        } else {
          this.patient = this.sanitizeData(data)
        }
        this.isFetching = false
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      }
    },
    onCompanySelected (company) {
      this.selectedCompany = company
      this.updatePatientData()
    },
    updatePatientData () {
      if (!this.selectedCompany || !this.allDetails.length) {
        // Default to first company if no selection
        const firstCompany = this.availableCompanies[0]
        if (firstCompany) {
          this.selectedCompany = firstCompany
        }
      }

      // Find patient data for selected company
      const patientFromSelectedCompany = this.allDetails.find(patient =>
        patient.company && patient.company.id === this.selectedCompany.id
      )

      if (patientFromSelectedCompany) {
        this.patient = this.sanitizePatientData(patientFromSelectedCompany)
      }
    },
    sanitizeData (item) {
      const data = item.results.length ? item.results[0].patients[0] : null
      return this.sanitizePatientData(data)
    },
    sanitizePatientData (data) {
      if (!data) return null

      return {
        ...data,
        formatted_birthday: data.dob ? this.displayDate(data.dob) : null,
        full_address: data.address ? this.getAddressInString(data.address) : null,
        capitalize_gender: data.gender ? this.$options.filters.capitalize(data.gender) : null,
        capitalize_nationality: data.nationality ? this.$options.filters.capitalize(data.nationality) : null,
        capitalize_race: data.race ? this.$options.filters.capitalize(data.race) : null,
        capitalize_religion: data.religion ? this.$options.filters.capitalize(data.religion) : null,
        since: data.record_date ? DateTime.fromISO(data.record_date).toFormat('d MMM yyyy') : null
      }
    },
    handlePopState() {
      this.toPatientDatabase()
      history.pushState(null, document.title, window.location.href)
    },
  },
}
</script>

<style scoped>
.main {
  position: absolute;
  top: 0;
  display: flex;
  flex-direction: column;
  width: 100% !important;
  height: 100% !important;
}
.analytic-border {
  border: 1px var(--v-neutral400) solid !important;
}
</style>